import { SsoRoleMapping } from '@choco-admin/types';
import React, { useState } from 'react';

import { BaseModal } from '../../../../components/BaseModal';
import { useNotification } from '../../../../contexts/NotificationContext';
import { useSsoRoleMappingDelete } from '../../hooks';
import { failureMessage } from '../../utils';

export interface DeleteRoleMappingModalProps {
  /** The mapping to remove. Null keeps the dialog closed. */
  roleMapping: SsoRoleMapping | null;
  onClose: () => void;
  /**
   * How many people on this connection currently arrive with this role. They
   * are the ones who lose their access, so the count is the whole warning.
   */
  affectedUserCount: number;
  /** False while the user list is still loading, so the count is not yet known. */
  isCountKnown: boolean;
}

export const DeleteRoleMappingModal: React.FC<DeleteRoleMappingModalProps> = ({
  roleMapping,
  onClose,
  affectedUserCount,
  isCountKnown,
}) => {
  const { addNotification } = useNotification();
  const { deleteRoleMapping, isDeleting } = useSsoRoleMappingDelete();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!roleMapping) {
    return null;
  }

  const handleClose = () => {
    setErrorMessage(null);
    onClose();
  };

  const handleConfirm = async () => {
    setErrorMessage(null);

    const result = await deleteRoleMapping({
      connectionId: roleMapping.connectionId,
      federatedRole: roleMapping.federatedRole,
    });

    if (result.kind !== 'success') {
      setErrorMessage(failureMessage(result));
      return;
    }

    addNotification({
      title: 'Role mapping removed',
      message: `"${roleMapping.federatedRole}" no longer gives access to ${roleMapping.supplierName ?? 'that distributor'}.`,
      type: 'success',
    });
    handleClose();
  };

  const distributor = roleMapping.supplierName ?? 'the distributor';

  const impact = (() => {
    if (!isCountKnown) {
      return `Anyone with this role loses access to ${distributor} the next time they sign in, and nothing tells them why.`;
    }
    if (affectedUserCount === 0) {
      return 'Nobody on this connection has this role right now.';
    }
    if (affectedUserCount === 1) {
      return `1 person on this connection has this role. They lose access to ${distributor} the next time they sign in, and nothing tells them why.`;
    }
    return `${affectedUserCount} people on this connection have this role. They lose access to ${distributor} the next time they sign in, and nothing tells them why.`;
  })();

  const isAnyoneAffected = !isCountKnown || affectedUserCount > 0;

  return (
    <BaseModal
      isOpen
      onClose={handleClose}
      size="md"
      title="Remove this role mapping?"
      footerContent={{
        onCancel: handleClose,
        onConfirm: handleConfirm,
        cancelText: 'Keep it',
        confirmText: 'Remove role mapping',
        isLoading: isDeleting,
        isDestructive: true,
        confirmColor: 'danger',
      }}
    >
      <div className="space-y-4">
        {errorMessage && (
          <div className="rounded-lg border border-danger-200 bg-danger-50 p-3">
            <p className="text-sm text-danger-700">{errorMessage}</p>
          </div>
        )}

        <div className="rounded-lg border border-default-200 bg-content2 p-4 space-y-1">
          <p className="font-mono text-sm text-foreground">
            {roleMapping.federatedRole}
          </p>
          <p className="text-sm text-default-500">
            {roleMapping.supplierName ?? 'Distributor no longer exists'}
          </p>
        </div>

        <p className="text-sm text-foreground">{impact}</p>
        {isAnyoneAffected && (
          <p className="text-sm text-default-500">
            Adding the mapping back restores their access.
          </p>
        )}
      </div>
    </BaseModal>
  );
};
